import type {NotificationToastMessage} from '@pexip/components';
import {notificationToastSignal, isValidIconName} from '@pexip/components';
import type {
    FormPayload,
    RPCCall,
    RPCCallReply,
    RPCReply,
} from '@pexip/plugin-api';
import {noop} from '@pexip/utils';

import type {Plugin} from '../branding';
import {getBrandingPath} from '../branding';

import type {PluginContext} from './types';
import {toggleWidgetSignal} from './signals/Iframe.signals';

const validateButton = (payload: RPCCall<'ui:button:add'>['payload']) => {
    let status: RPCCallReply<'ui:button:add'>['status'] = 'ok';
    if (
        payload.position !== 'settingsMenu' &&
        typeof payload.icon === 'string' &&
        !isValidIconName(payload.icon)
    ) {
        status = 'failed';
        return {status, reason: 'Invalid Icon name' as const};
    }
    return {status};
};

export const handleAddButton = (
    data: RPCCall<'ui:button:add'>,
    setPluginsElements: React.Dispatch<React.SetStateAction<PluginContext>>,
): Omit<RPCReply<'ui:button:add'>, 'chanId'> => {
    const replyMeta = {
        rpc: data.rpc,
        replyTo: data.id,
    };
    const {status, reason} = validateButton(data.payload);
    if (status === 'failed') {
        return {
            ...replyMeta,
            payload: {
                status,
                reason,
                id: data.id,
            },
        };
    }

    setPluginsElements(prevPluginsElements => {
        const pluginsElements = structuredClone(prevPluginsElements);
        const pluginElements = pluginsElements[data.chanId];
        if (!pluginElements) {
            return prevPluginsElements;
        }

        const button = {
            ...data.payload,
            id: data.id,
            chanId: data.chanId,
        };
        pluginElements.buttons.push(button);
        return pluginsElements;
    });
    return {
        ...replyMeta,
        payload: {
            status,
            id: data.id,
            data: undefined,
        },
    };
};

export const handleUpdateButton = (
    data: RPCCall<'ui:button:update'>,
    setPluginsElements: React.Dispatch<React.SetStateAction<PluginContext>>,
): Omit<RPCReply<'ui:button:update'>, 'chanId'> => {
    const replyMeta = {
        rpc: data.rpc,
        replyTo: data.id,
    };
    const {status, reason} = validateButton(data.payload);
    if (status === 'failed') {
        return {
            ...replyMeta,
            payload: {
                status,
                reason,
                id: data.id,
            },
        };
    }
    setPluginsElements(prevPluginsElements => {
        const pluginsElements = structuredClone(prevPluginsElements);
        const pluginElements = pluginsElements[data.chanId];
        if (!pluginElements) {
            return prevPluginsElements;
        }
        const btnIndex = pluginElements.buttons.findIndex(
            btn => btn.id === data.payload.targetId,
        );
        if (btnIndex > -1 && typeof data.id === 'string') {
            pluginElements.buttons[btnIndex] = {
                ...data.payload,
                id: data.payload.targetId,
                chanId: data.chanId,
            };
        }
        return pluginsElements;
    });
    return {
        ...replyMeta,
        payload: {
            status: 'ok',
            id: data.id,
            data: undefined,
        },
    };
};

const validateForm = (payload: FormPayload) => {
    let status: RPCCallReply<'ui:form:open'>['status'] = 'ok';
    let reason = '';
    const selectElements = Object.values(payload.form.elements).filter(
        element => element.type === 'select',
    );

    if (selectElements.find(element => element.options.length === 0)) {
        status = 'failed';
        reason =
            'The select elements in the form must have at least one option';
    } else if (
        selectElements.find(
            el =>
                el.selected &&
                !el.options.map(el => el.id).includes(el.selected),
        )
    ) {
        status = 'failed';
        reason = `A SelectElement's 'selected' value must be one of its options' IDs`;
    }
    return {
        status,
        reason,
    };
};

export const handleOpenForm = (
    data: RPCCall<'ui:form:open'>,
    setPluginsElements: React.Dispatch<React.SetStateAction<PluginContext>>,
): Omit<RPCReply<'ui:form:open'>, 'chanId'> => {
    const replyMeta = {
        rpc: data.rpc,
        replyTo: data.id,
    };
    const {status, reason} = validateForm(data.payload);
    if (status === 'failed') {
        return {
            ...replyMeta,
            payload: {
                status,
                reason,
                id: data.id,
            },
        };
    }
    setPluginsElements(prevPluginsElements => {
        const pluginsElements = structuredClone(prevPluginsElements);
        const pluginElements = pluginsElements[data.chanId];
        if (!pluginElements) {
            return prevPluginsElements;
        }

        const form = {
            ...data.payload,
            id: data.id,
            chanId: data.chanId,
        };
        pluginElements.forms.push(form);
        return pluginsElements;
    });
    return {
        rpc: data.rpc,
        replyTo: data.id,
        payload: {
            status,
            id: data.id,
            data: undefined,
        },
    };
};

export const handleOpenPrompt = (
    data: RPCCall<'ui:prompt:open'>,
    setPluginsElements: React.Dispatch<React.SetStateAction<PluginContext>>,
): Omit<RPCReply<'ui:prompt:open'>, 'chanId'> => {
    setPluginsElements(prevPluginsElements => {
        const pluginsElements = structuredClone(prevPluginsElements);
        const pluginElements = pluginsElements[data.chanId];
        if (!pluginElements) {
            return prevPluginsElements;
        }

        const prompt = {
            ...data.payload,
            id: data.id,
            chanId: data.chanId,
        };
        pluginElements.prompts.push(prompt);
        return pluginsElements;
    });
    return {
        rpc: data.rpc,
        replyTo: data.id,
        payload: {
            status: 'ok',
            id: data.id,
            data: undefined,
        },
    };
};

export const handleRemoveElement = (
    data: RPCCall<'ui:removeElement'>,
    setPluginsElements: React.Dispatch<React.SetStateAction<PluginContext>>,
): Omit<RPCReply<'ui:removeElement'>, 'chanId'> => {
    setPluginsElements(prevPluginsElements => {
        const pluginsElements = structuredClone(prevPluginsElements);
        const pluginElements = pluginsElements[data.chanId];
        if (!pluginElements) {
            return prevPluginsElements;
        }
        for (let group of Object.values(pluginElements)) {
            const elIndex = group.findIndex(el => el.id === data.payload.id);
            if (elIndex > -1) {
                group = group.splice(elIndex, 1);
            }
        }
        return pluginsElements;
    });
    return {
        rpc: data.rpc,
        replyTo: data.id,
        payload: {
            status: 'ok',
            id: data.id,
            data: undefined,
        },
    };
};

export const handleShowToast = (
    data: RPCCall<'ui:toast:show'>,
): Omit<RPCReply<'ui:toast:show'>, 'chanId'> => {
    const {canDismiss, ...rest} = data.payload;
    const toastData: NotificationToastMessage = {
        ...rest,
        ...(canDismiss && {onDismiss: noop}),
    };
    notificationToastSignal.emit([toastData]);
    return {
        rpc: data.rpc,
        replyTo: data.id,
        payload: {
            status: 'ok',
            id: data.id,
            data: undefined,
        },
    };
};

export const handleAddWidget = (
    data: RPCCall<'ui:widget:add'>,
    setPluginsElements: React.Dispatch<React.SetStateAction<PluginContext>>,
    manifestPlugin?: Plugin,
): Omit<RPCReply<'ui:widget:add'>, 'chanId'> => {
    if (!manifestPlugin) {
        return {
            rpc: data.rpc,
            replyTo: data.id,
            payload: {
                status: 'failed',
                id: data.id,
                reason: "Widgets require that the 'id' of the plugin in the branding manifest matches the 'id' passed in 'registerPlugin'",
            },
        };
    }
    setPluginsElements(prevPluginsElements => {
        const pluginsElements = structuredClone(prevPluginsElements);
        const pluginElements = pluginsElements[data.chanId];
        if (!pluginElements) {
            return prevPluginsElements;
        }

        let widgetSrc = data.payload.src;
        try {
            new URL(widgetSrc);
        } catch {
            widgetSrc = getBrandingPath(widgetSrc);
        }

        const widget = {
            ...data.payload,
            src: widgetSrc,
            id: data.id,
            chanId: data.chanId,
            manifestPlugin,
        };
        pluginElements.widgets.push(widget);
        return pluginsElements;
    });
    return {
        rpc: data.rpc,
        replyTo: data.id,
        payload: {
            status: 'ok',
            id: data.id,
            data: undefined,
        },
    };
};

export const handleToggleWidget = (
    data: RPCCall<'ui:widget:toggle'>,
): Omit<RPCReply<'ui:widget:toggle'>, 'chanId'> => {
    toggleWidgetSignal.emit({widgetId: data.payload.id});
    return {
        rpc: data.rpc,
        replyTo: data.id,
        payload: {
            status: 'ok',
            id: data.id,
            data: undefined,
        },
    };
};
